import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEmployeeInformation,
  updateIdField,
  setIdImage,
  removeIdImage,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./IdVerification.module.scss";

const IdVerification = forwardRef(function IdVerification(_, ref) {
  const dispatch = useDispatch();

  const {
    idVerification,
    basicInformation,
    basicImages,
    addressImages,
    idVerificationImages,
  } = useSelector(selectEmployeeInformation);

  const [errorFields, setErrorFields] = useState(new Set());

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const updateField = (name, value) => {
    dispatch(updateIdField({ name, value }));
    clearError(name);
  };

  const handleImageChange = (name, file) => {
    if (!file || file.size > 10 * 1024 * 1024) {
      return;
    }

    if (idVerificationImages[name]?.url) {
      URL.revokeObjectURL(idVerificationImages[name].url);
    }

    dispatch(
      setIdImage({
        name,
        image: {
          name: file.name,
          file,
          url: URL.createObjectURL(file),
        },
      }),
    );

    // Store preview URL in Redux state using legacy keys
    if (name === "pan") {
      updateField("pan_id", URL.createObjectURL(file));
    }

    clearError(name);
  };

  const removeImage = (name) => {
    if (idVerificationImages[name]?.url) {
      URL.revokeObjectURL(idVerificationImages[name].url);
    }

    dispatch(removeIdImage(name));

    if (name === "pan") {
      updateField("pan_id", "");
      updateField("pan", "");
    }
  };

  const getMissingFields = useCallback(() => {
    const missing = [];
    const missingNames = new Set();

    // Aadhaar Number from Basic Information
    const aadharNumber =
      basicInformation.aadhar_number || basicInformation.aadhaar_number || "";

    if (String(aadharNumber).replace(/\D/g, "").length !== 12) {
      missing.push("Aadhaar Number");
      missingNames.add("aadhar_number");
    }

    // Aadhaar Front from Basic Information
    if (!basicImages.aadhaarFront) {
      missing.push("Aadhaar Front Image");
      missingNames.add("aadharfront_id");
    }

    // Aadhaar Back from Address Details
    if (!addressImages.aadhaarBack) {
      missing.push("Aadhaar Back Image");
      missingNames.add("aadharback_id");
    }

    // PAN Number
    if (!String(idVerification.pan_number || "").trim()) {
      missing.push("PAN Number");
      missingNames.add("pan_number");
    }

    // PAN Image
    if (!idVerificationImages.pan && !idVerification.pan_id) {
      missing.push("PAN Image");
      missingNames.add("pan");
    }

    setErrorFields(missingNames);

    return missing;
  }, [
    basicInformation,
    basicImages,
    addressImages,
    idVerification,
    idVerificationImages,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }),
    [getMissingFields],
  );

  const renderUpload = (name, label) => {
    const image = idVerificationImages[name];
    const hasError = errorFields.has(name);

    return (
      <div className={styles.uploadBlock}>
        <span>
          {label}
          <b>*</b>
        </span>

        <div className={styles.previewRow}>
          {image ? (
            <>
              <img
                className={styles.imagePreview}
                src={image.url}
                alt={`${label} preview`}
              />
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => removeImage(name)}
              >
                Remove Image
              </button>
            </>
          ) : idVerification.pan_id && name === "pan" ? (
            <>
              <img
                className={styles.imagePreview}
                src={idVerification.pan_id}
                alt="PAN preview"
              />
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => removeImage(name)}
              >
                Remove Image
              </button>
            </>
          ) : (
            <label className={styles.uploadButton}>
              Upload Image
              <input
                className={styles.hiddenInput}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={(event) =>
                  handleImageChange(name, event.target.files?.[0])
                }
              />
            </label>
          )}
        </div>

        {hasError && (
          <p className={styles.fieldError}>Please upload this image</p>
        )}
      </div>
    );
  };

  const aadharNumber =
    basicInformation.aadhar_number || basicInformation.aadhaar_number || "";

  return (
    <div className={styles.idVerification}>
      <header className={styles.sectionHeader}>ID Verification</header>

      <div className={styles.content}>
        <div className={styles.grid}>
          <label className={styles.field}>
            <span>
              Aadhaar Card Number<b>*</b>
            </span>
            <input
              value={
                aadharNumber
                  ? `**** **** ${String(aadharNumber).slice(-4)}`
                  : ""
              }
              disabled
              className={
                errorFields.has("aadhar_number") ? styles.inputError : ""
              }
            />
            {errorFields.has("aadhar_number") && (
              <p className={styles.fieldError}>
                Please enter a valid 12-digit Aadhaar number in Basic
                Information
              </p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              PAN Card Number<b>*</b>
            </span>
            <input
              value={idVerification.pan_number || ""}
              maxLength={10}
              className={errorFields.has("pan_number") ? styles.inputError : ""}
              onChange={(event) =>
                updateField(
                  "pan_number",
                  event.target.value.toUpperCase().slice(0, 10),
                )
              }
            />
            {errorFields.has("pan_number") && (
              <p className={styles.fieldError}>Please enter your PAN number</p>
            )}
          </label>

          <div className={styles.uploadBlock}>
            <span>
              Aadhaar Front<b>*</b>
            </span>
            {basicImages.aadhaarFront ? (
              <img
                className={styles.imagePreview}
                src={basicImages.aadhaarFront.url}
                alt="Aadhaar Front"
              />
            ) : (
              <p className={styles.fieldError}>
                Please upload Aadhaar Front in Basic Information
              </p>
            )}
          </div>

          <div className={styles.uploadBlock}>
            <span>
              Aadhaar Back<b>*</b>
            </span>
            {addressImages.aadhaarBack ? (
              <img
                className={styles.imagePreview}
                src={addressImages.aadhaarBack.url}
                alt="Aadhaar Back"
              />
            ) : (
              <p className={styles.fieldError}>
                Please upload Aadhaar Back in Address Details
              </p>
            )}
          </div>

          {renderUpload("pan", "Photo of PAN Card")}
        </div>
      </div>
    </div>
  );
});

export default IdVerification;
